import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { login, setToken, getToken } from "@/api";
import { toast } from "sonner";
import { Terminal, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (getToken()) navigate("/admin/dashboard");
  }, [navigate]);

  const formatErr = (detail) => {
    if (!detail) return "Login failed";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(", ");
    return String(detail);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { token } = await login(email, password);
      setToken(token);
      toast.success("Access granted // welcome back");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6 relative scanlines noise-overlay overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20" />

      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/"
          data-testid="back-link"
          className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] hover:text-[var(--accent)] flex items-center gap-2 mb-8"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>

        <div className="corner-ticks bg-[#0a0a0a] border border-[var(--line-strong)] p-8 md:p-10">
          <div className="bracket mb-2">[ ADMIN // PORTAL ]</div>
          <h1
            data-text="LOGIN"
            className="glitch font-display text-4xl md:text-5xl font-extrabold uppercase tracking-tight mb-8"
          >
            LOGIN
          </h1>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                &gt; email
              </label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-4 py-3 font-mono text-sm text-white"
                placeholder="admin@example.com"
                autoComplete="username"
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                &gt; password
              </label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-4 py-3 font-mono text-sm text-white"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="btn-accent w-full justify-center mt-2 disabled:opacity-50"
            >
              <Terminal size={14} /> {busy ? "AUTHENTICATING..." : "AUTHENTICATE"}
            </button>
          </form>
        </div>

        <div className="mt-6 font-mono text-[10px] text-[var(--muted)] tracking-widest text-center">
          // ENCRYPTED CHANNEL — UNAUTHORIZED ACCESS LOGGED
        </div>
      </div>
    </div>
  );
}
